import { normalizeAthenaError, withRetry } from './auxiliaries.ts'
import type {
  AthenaOperationContext,
  NormalizedAthenaError,
  RetryConfig,
} from './auxiliaries.ts'
import type {
  AthenaGatewayErrorDetails,
  AthenaGatewayResponse,
} from './gateway/types.ts'

const ATHENA_NORMALIZED_ERROR_KEY = '__athenaNormalizedError' as const

const READ_RETRY_CONFIG: RetryConfig = {
  retries: 2,
  baseDelayMs: 100,
  maxDelayMs: 1_000,
  backoff: 'exponential',
  jitter: true,
}

export interface AthenaResult<T> {
  data: T | null
  error: AthenaResultError | null
  statusText?: string | null
  /**
   * @deprecated Prefer `error?.gatewayCode`, `error?.hint`, and related fields on `error`.
   */
  errorDetails?: AthenaGatewayErrorDetails | null
  status: number
  count?: number | null
  raw: unknown
}

export interface AthenaResultError {
  message: string
  code: string | null
  athenaCode: NormalizedAthenaError['code']
  gatewayCode?: AthenaGatewayErrorDetails['code'] | null
  kind: NormalizedAthenaError['kind']
  category: NormalizedAthenaError['category']
  retryable: boolean
  details: unknown | null
  hint: string | null
  status: number
  statusText: string | null
  constraint?: string
  table?: string
  operation?: string
  endpoint?: AthenaGatewayErrorDetails['endpoint']
  method?: AthenaGatewayErrorDetails['method']
  requestId?: string
  cause?: string
  raw: unknown
}

export type AthenaResultFormatter = <T>(
  response: AthenaGatewayResponse<T>,
  context?: AthenaOperationContext,
) => AthenaResult<T>

function formatResult<T>(response: AthenaGatewayResponse<T>): AthenaResult<T> {
  const result: AthenaResult<T> = {
    data: response.data ?? null,
    error: null,
    errorDetails: response.errorDetails ?? null,
    status: response.status,
    statusText: response.statusText ?? null,
    raw: response.raw,
  }
  if (response.count !== undefined) {
    result.count = response.count
  }
  return result
}

function attachNormalizedError<T>(
  result: AthenaResult<T>,
  normalizedError: NormalizedAthenaError,
): void {
  Object.defineProperty(result, ATHENA_NORMALIZED_ERROR_KEY, {
    value: normalizedError,
    enumerable: false,
    configurable: true,
    writable: false,
  })
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim()
    }
  }
  return undefined
}

function resolveStructuredErrorPayload(raw: unknown): Record<string, unknown> | null {
  if (!isRecord(raw)) return null
  return isRecord(raw.error) ? raw.error : raw
}

function resolveStructuredErrorDetails(
  payload: Record<string, unknown> | null,
  message: string,
): unknown | null {
  if (!payload || !('details' in payload)) {
    return null
  }
  const details = payload.details
  if (details == null) {
    return null
  }
  if (typeof details === 'string' && details.trim() === message.trim()) {
    return null
  }
  return details
}

function createResultError<T>(
  response: AthenaGatewayResponse<T>,
  result: AthenaResult<T>,
  normalized: NormalizedAthenaError,
): AthenaResultError {
  const rawRecord = isRecord(response.raw) ? response.raw : null
  const payload = resolveStructuredErrorPayload(response.raw)
  const message =
    firstNonEmptyString(
      response.error,
      payload?.message,
      payload?.error,
      payload?.details,
      response.errorDetails?.message,
      normalized.message,
    ) ?? normalized.message
  const statusText = firstNonEmptyString(response.statusText, rawRecord?.statusText) ?? null
  const hint = firstNonEmptyString(payload?.hint, response.errorDetails?.hint) ?? null
  const code = firstNonEmptyString(payload?.code) ?? normalized.code
  const details =
    resolveStructuredErrorDetails(payload, message) ?? response.errorDetails?.cause ?? null

  return {
    message,
    code,
    athenaCode: normalized.code,
    gatewayCode: response.errorDetails?.code ?? null,
    kind: normalized.kind,
    category: normalized.category,
    retryable: normalized.retryable,
    details,
    hint,
    status: result.status,
    statusText,
    constraint: normalized.constraint,
    table: normalized.table,
    operation: normalized.operation,
    endpoint: response.errorDetails?.endpoint,
    method: response.errorDetails?.method,
    requestId: response.errorDetails?.requestId,
    cause: response.errorDetails?.cause,
    raw: result.raw,
  }
}

export function createResultFormatter(): AthenaResultFormatter {
  return <T>(
    response: AthenaGatewayResponse<T>,
    context?: AthenaOperationContext,
  ): AthenaResult<T> => {
    const result = formatResult(response)
    if (response.error == null && response.errorDetails == null) {
      return result
    }
    const normalizedError = normalizeAthenaError(
      {
        ...result,
        error: response.error ?? response.errorDetails?.message ?? null,
      },
      context,
    )
    result.error = createResultError(response, result, normalizedError)
    attachNormalizedError(result, normalizedError)
    return result
  }
}

export async function executeRead<T>(
  behavior: { retryReads?: boolean } | undefined,
  runner: () => Promise<AthenaResult<T>>,
): Promise<AthenaResult<T>> {
  if (!behavior?.retryReads) {
    return runner()
  }

  let lastRetryableResult: AthenaResult<T> | undefined
  let lastRetrySignal: AthenaResultError | null = null
  try {
    return await withRetry(
      {
        ...READ_RETRY_CONFIG,
        shouldRetry: error =>
          error === lastRetrySignal || normalizeAthenaError(error).retryable,
      },
      async () => {
        const result = await runner()
        if (result.error?.retryable) {
          lastRetryableResult = result
          lastRetrySignal = result.error
          throw lastRetrySignal
        }
        return result
      },
    )
  } catch (error) {
    if (lastRetryableResult && error === lastRetrySignal) {
      return lastRetryableResult
    }
    throw error
  }
}

export function toSingleResult<Result>(
  response: AthenaResult<Result>,
): AthenaResult<Result extends Array<infer Item> ? Item | null : Result | null> {
  const payload = response.data
  const singleData = Array.isArray(payload)
    ? payload.length
      ? payload[0]
      : null
    : (payload ?? null)
  return {
    ...response,
    data: singleData as Result extends Array<infer Item> ? Item | null : Result | null,
  }
}

